import { Anchor, Badge, Group, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UnifyTable, type UnifyTableColumn, type UnifyTableMultiFilterConfig } from '@/components/UnifyTable';
import { formatDateTime } from './components/billingFormatters';
import { MOCK_BATCH_RUNS, type BatchRunRow, type BatchRunStatus } from './mock/mockBatchRuns';

const STATUS_COLORS: Record<BatchRunStatus, string> = {
  completed: 'green',
  partial: 'yellow',
  failed: 'red',
  running: 'blue',
};

export function BatchRunsPage(): JSX.Element {
  const [opened, { open, close }] = useDisclosure(false);
  const [selected, setSelected] = useState<BatchRunRow | null>(null);

  const columns = useMemo(
    (): UnifyTableColumn<BatchRunRow>[] => [
      {
        key: 'startedAt',
        label: 'Started',
        render: (row) => <Text size="sm">{formatDateTime(row.startedAt)}</Text>,
        getValue: (row) => row.startedAt,
      },
      {
        key: 'status',
        label: 'Status',
        render: (row) => (
          <Badge variant="light" size="sm" color={STATUS_COLORS[row.status]}>
            {row.status}
          </Badge>
        ),
        getValue: (row) => row.status,
      },
      {
        key: 'submitted',
        label: 'Submitted',
        align: 'right',
        render: (row) => <Text size="sm">{row.submittedCount}</Text>,
        getValue: (row) => String(row.submittedCount).padStart(4, '0'),
      },
      {
        key: 'failed',
        label: 'Failed',
        align: 'right',
        render: (row) => <Text size="sm">{row.failedCount}</Text>,
        getValue: (row) => String(row.failedCount).padStart(4, '0'),
      },
    ],
    [],
  );

  const multiFilters = useMemo(
    (): UnifyTableMultiFilterConfig[] => [
      {
        columnKey: 'status',
        allLabel: 'All statuses',
        placeholder: 'Status',
        data: ['completed', 'partial', 'failed', 'running'],
        emptyMeansAll: true,
      },
    ],
    [],
  );

  return (
    <>
      <UnifyTable
        title="Batch Runs"
        subtitle="Nightly claim submission history"
        columns={columns}
        data={MOCK_BATCH_RUNS}
        getRowKey={(row) => row.id}
        searchable={false}
        sortable
        pagination
        defaultRowsPerPage={10}
        multiFilters={multiFilters}
        onRowClick={(row) => {
          setSelected(row);
          open();
        }}
      />

      <Modal opened={opened} onClose={close} title={`Batch ${selected?.id ?? ''}`} size="lg" centered>
        {selected && (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Started {formatDateTime(selected.startedAt)}
            </Text>
            {selected.claimOutcomes.map((outcome) => (
              <Group key={outcome.claimId} justify="space-between" wrap="nowrap">
                <Stack gap={0}>
                  <Text size="sm" fw={600}>
                    {outcome.clientName}
                  </Text>
                  <Anchor
                    component={Link}
                    to={`/dashboard/billing/claims/${encodeURIComponent(outcome.claimId)}`}
                    size="xs"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {outcome.claimId}
                  </Anchor>
                </Stack>
                <Stack gap={0} align="flex-end">
                  <Badge
                    variant="light"
                    size="sm"
                    color={outcome.outcome === 'submitted' ? 'green' : 'red'}
                  >
                    {outcome.outcome}
                  </Badge>
                  {outcome.errorSummary && (
                    <Text size="xs" c="dimmed" ta="right">
                      {outcome.errorSummary}
                    </Text>
                  )}
                </Stack>
              </Group>
            ))}
          </Stack>
        )}
      </Modal>
    </>
  );
}
